/**
 * RightsTab Component
 *
 * Display user's computed permissions/rights (read-only).
 * Features: view permission names, search/filter.
 */

import { useState, useEffect, useMemo } from 'react';
import { IconKey, IconRefresh, IconAlertCircle, IconSearch } from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  Table,
  Badge,
  Text,
  TextInput,
  EmptyState,
  Loader,
  Center,
  Alert,
  ScrollArea,
} from '../../../components/ui';
import { useUserService } from '@actschurch/ccms-sdk';
import type { ProfileTabProps } from '../types';

export function RightsTab({ userId, user }: ProfileTabProps) {
  const userService = useUserService();

  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Fetch user permissions
   */
  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);

    try {
      const userPermissions = await userService.getUserPermissions(userId);
      setPermissions(userPermissions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load permissions');
      console.error('[RightsTab] Error fetching permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchPermissions();
    }
  }, [userId]);

  /**
   * Filter permissions based on search query
   */
  const filteredPermissions = useMemo(() => {
    if (!searchQuery.trim()) return permissions;

    const query = searchQuery.toLowerCase();
    return permissions.filter((permission) =>
      permission.toLowerCase().includes(query)
    );
  }, [permissions, searchQuery]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    fetchPermissions();
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error">
        {error}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                Permissions & Rights
              </Text>
              <Text size="xs" c="dimmed">
                Computed permissions derived from your assigned roles
              </Text>
            </div>
            <Group gap="xs">
              <Badge size="lg" variant="light" color="grape" leftSection={<IconKey size={14} />}>
                {filteredPermissions.length} of {permissions.length} {permissions.length === 1 ? 'Permission' : 'Permissions'}
              </Badge>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={loading}
                size="sm"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {/* Search Input */}
          {permissions.length > 0 && (
            <TextInput
              placeholder="Search permissions..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
            />
          )}

          {/* Permissions Table */}
          {filteredPermissions.length > 0 ? (
            <ScrollArea>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Permission Name</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredPermissions.map((permission, index) => (
                    <Table.Tr key={`${permission}-${index}`}>
                      <Table.Td>
                        <Group gap="xs">
                          <IconKey size={16} color="var(--mantine-color-grape-6)" />
                          <Text size="sm" ff="monospace">
                            {permission}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          ) : permissions.length > 0 ? (
            <EmptyState
              icon={<IconSearch size={64} />}
              title="No matching permissions"
              description={`No permissions found matching "${searchQuery}"`}
              size="md"
            />
          ) : (
            <EmptyState
              icon={<IconKey size={64} />}
              title="No permissions assigned"
              description="You don't have any permissions assigned yet. Permissions are derived from your roles."
              size="md"
            />
          )}
        </Stack>
      </Card>

      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">
          Permissions are automatically computed from your assigned roles and control what actions you can perform in the system. To modify your permissions, an administrator needs to update your roles.
        </Text>
      </Alert>
    </Stack>
  );
}
